import { Suspense } from "react";
import { useGlobalSearchParams } from "expo-router";
import { Card, MatchCase } from "@ptsd/uikit";
import { useMedia, View } from "tamagui";

import type { TSpheres } from "@edenlabllc/ptsd-validators";

import { api } from "~/utils/api";
import { LoadingScreen } from "~/component/blocks";
import { Howareu } from "~/component/blocks/howareu";
import {
  ComposedTextMain,
  ComposedTextMobile,
  ComposedTextMobileSphere,
  ComposedTextSphere,
  CriticalScales,
  MentalBalance,
  MentalBalanceScales,
  RecommendationMobile,
  Recommendations,
  ResultsDisclaimer,
} from "~/component/feature";
import { CallToDoctor } from "~/component/feature/home/call-to-doctor";
import { PaymentCard } from "~/component/feature/paywall/payment-card";
import { useConstructRecommendations, useConstuctComposedText } from "~/hooks";
import { useTrackOnce } from "~/hooks/use-track";

const HMT_DOMAIN = "hmt";

const Results = () => {
  const { sm } = useMedia();
  useTrackOnce("Render Results Page");

  return (
    <MatchCase value={sm}>
      {(Case) => (
        <>
          <Case condition={false}>
            <Suspense fallback={<LoadingScreen height={"70dvh"} />}>
              <ResultsDefault />
            </Suspense>
          </Case>

          <Case condition={true}>
            <Suspense fallback={<LoadingScreen height={"70dvh"} />}>
              <ResultsMobile />
            </Suspense>
          </Case>
        </>
      )}
    </MatchCase>
  );
};

export default Results;

const ResultsDefault = () => {
  const slug = useGlobalSearchParams().slug as unknown as TSpheres;

  const [data] = api.scores.bySphere.useSuspenseQuery({
    domain: slug === HMT_DOMAIN ? undefined : slug,
  });

  const recommendations = useConstructRecommendations(data.recommendations);
  const composedText = useConstuctComposedText(data);

  return (
    <MatchCase value={slug}>
      {(Case, Default) => (
        <>
          <ResultsDisclaimer />
          <CallToDoctor />
          <Case condition={HMT_DOMAIN}>
            <ComposedTextMain {...composedText} />
            <PaymentCard isPaywall={data.isPaywall} />
            <CriticalScales />
            <MentalBalance disabledLabelLink={data.isPaywall} />
          </Case>

          <Case condition={(value) => value !== HMT_DOMAIN}>
            <ComposedTextSphere {...composedText} />
            <MentalBalanceScales data={data?.scales} />
          </Case>

          {(recommendations?.length ?? 0) > 0 && (
            <Default>
              <Card shadow="md" gap={"$4"}>
                <Card.Title>Рекомендації для вашого стану</Card.Title>
                <Recommendations layout="results" data={recommendations} />
              </Card>
            </Default>
          )}

          <Case condition={HMT_DOMAIN}>
            <Howareu />
          </Case>
        </>
      )}
    </MatchCase>
  );
};

const ResultsMobile = () => {
  const slug = useGlobalSearchParams().slug as unknown as TSpheres;
  const domain = slug === HMT_DOMAIN ? undefined : slug;
  const [data] = api.scores.bySphere.useSuspenseQuery({ domain });

  const recommendations = useConstructRecommendations(data.recommendations);

  const composedText = useConstuctComposedText(data);

  return (
    <MatchCase value={slug}>
      {(Case, Default) => (
        <>
          <ResultsDisclaimer />
          <CallToDoctor marginTop={"$2"} marginHorizontal={"$1"} />
          <Case condition={HMT_DOMAIN}>
            <ComposedTextMobile {...composedText} />
            <PaymentCard isPaywall={data.isPaywall} />
            <CriticalScales />
            <MentalBalance disabledLabelLink={data.isPaywall} />
          </Case>

          <Case condition={(value) => value !== HMT_DOMAIN}>
            <ComposedTextMobileSphere {...composedText} />
            <MentalBalanceScales data={data?.scales} />
          </Case>

          {(recommendations?.length ?? 0) > 0 && (
            <Default>
              <View gap={"$4.2"} padding={"$3"} paddingVertical={"$5"}>
                <Card.Title>Рекомендації для вашого стану</Card.Title>
                <RecommendationMobile data={recommendations} />
              </View>
            </Default>
          )}

          <Case condition={HMT_DOMAIN}>
            <View paddingHorizontal="$3" paddingTop="$4" paddingBottom="$6">
              <Howareu />
            </View>
          </Case>
        </>
      )}
    </MatchCase>
  );
};
